import {stoppageToggleButtonGroupSx} from '@hconnect/common/styles'
import {mergeSx} from '@hconnect/uikit'
import {
  Dialog,
  DialogContent,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery
} from '@mui/material'
import React, {useState, FC, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../../consts'
import {useApproversAdjustFormData} from '../../../hooks/useApproversAdjustFormData'

import {ApproverGroups} from './ApproverGroups'
import {ApproverMatrix} from './ApproverMatrix'

export enum ApproversAdjustTab {
  Matrix = 'matrix',
  Groups = 'groups'
}

export const tabs: ApproversAdjustTab[] = [ApproversAdjustTab.Matrix, ApproversAdjustTab.Groups]
const TRANSLATION_KEYS: Record<ApproversAdjustTab, string> = {
  [ApproversAdjustTab.Matrix]: 'approversAdjust.label.approverMatrix',
  [ApproversAdjustTab.Groups]: 'approversAdjust.label.approverGroups'
}
const MOBILE_TRANSLATION_KEYS: Record<ApproversAdjustTab, string> = {
  [ApproversAdjustTab.Matrix]: 'approversAdjust.label.matrix',
  [ApproversAdjustTab.Groups]: 'approversAdjust.label.groups'
}
const DEFAULT_TAB = ApproversAdjustTab.Matrix

type ApproversAdjustDialogProps = {
  plantId: string
  handleClose: (data?: {isSuccess: boolean}) => void
}

export const ApproversAdjustDialog: FC<ApproversAdjustDialogProps> = ({plantId, handleClose}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const [tab, setTab] = useState<ApproversAdjustTab>(DEFAULT_TAB)

  const {
    isSavingGroups,
    isSavingMatrix,
    approverGroupsQuery,
    changeFunctionApproverGroupApprovalRequirementsQuery,
    control,
    handleSubmit,
    onSubmit,
    currentValues,
    matrixData,
    setMatrixData
  } = useApproversAdjustFormData({plantId, handleClose})

  const tabComponents: Record<ApproversAdjustTab, ReactNode> = {
    [ApproversAdjustTab.Matrix]: (
      <ApproverMatrix
        approverGroupsQuery={approverGroupsQuery}
        onClose={handleClose}
        handleSave={onSubmit}
        isSaving={isSavingMatrix}
        changeFunctionApproverGroupApprovalRequirementsQuery={
          changeFunctionApproverGroupApprovalRequirementsQuery
        }
        data={matrixData}
        setData={setMatrixData}
      />
    ),
    [ApproversAdjustTab.Groups]: (
      <ApproverGroups
        plantId={plantId}
        onClose={handleClose}
        approverGroupsQuery={approverGroupsQuery}
        isPending={isSavingGroups}
        currentValues={currentValues}
        control={control}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />
    )
  }

  return (
    <Dialog
      data-test-id="approvers-adjust-dialog"
      open={true}
      onClose={() => handleClose()}
      fullWidth
      maxWidth={tab === ApproversAdjustTab.Matrix ? 'lg' : 'md'}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          ...(isMobile && {p: 2})
        }}
      >
        <ToggleButtonGroup
          color="primary"
          sx={mergeSx({mb: 1.5}, stoppageToggleButtonGroupSx)}
          fullWidth
          exclusive
          value={tab}
          onChange={(event: React.MouseEvent<HTMLElement>, value: ApproversAdjustTab) => {
            if (value) setTab(value)
          }}
        >
          {tabs.map((tab) => (
            <ToggleButton key={tab} value={tab} data-test-id={`approvers-dialog-tab-${tab}`}>
              {t(isMobile ? MOBILE_TRANSLATION_KEYS[tab] : TRANSLATION_KEYS[tab])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {tabComponents[tab]}
      </DialogContent>
    </Dialog>
  )
}
